import React, {useMemo, useState, useCallback, useEffect, useRef} from "react";
import {Button, ConfigProvider, Dropdown, Layout, Menu, MenuProps} from "antd";
import {
    CalendarOutlined,
    HomeOutlined,
    MenuOutlined,
    MoneyCollectFilled,
    UserAddOutlined,
    UserOutlined,
    BarChartOutlined,
    CheckSquareOutlined,
    TeamOutlined,
    SyncOutlined,
    LogoutOutlined,
    DownOutlined,
    CompassOutlined,
} from "@ant-design/icons";
import {
    BrowserRouter as Router,
    Link,
    Navigate,
    Route,
    Routes,
    useLocation,
    useNavigate,
} from "react-router-dom";
import MembershipValidationModal from "./components/membership/MembershipValidationModal";
import {BookingServiceProvider} from "./services/bookings/BookingServiceProvider";
import {MemberServiceProvider} from "./services/members/MemberServiceProvider";
import {PaymentServiceProvider} from "./services/payments/PaymentServiceProvider";
import {RecurringMemberItemsServiceProvider} from "./services/recurring/RecurringMemberItemsServiceProvider";
import {withAuthenticationRequired, useAuth0} from "@auth0/auth0-react";
import Auth0ProviderWithConfig from "./components/login/Auth0ProviderWithConfig";
import ClubsPage from "./components/login/ClubsPage";
import LandingPage from "./components/login/LandingPage";
import {extractSubdomainSlug} from "./utils/extractSubdomainSlug";
import DirectDebitManagement from "./components/directdebit/DirectDebitManagement";
import DirectDebitRunManager from "./components/directdebit/DirectDebitRunManager";
import {FacilityBookingScheduleV2} from "./components/bookings/v2/FacilityBookingScheduleV2";
import BookingPricingManagement from "./components/bookings/pricing/BookingPricingManagement";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import {ClientProvider} from "./common/ClientProvider";
import {StaffIdentityProvider} from "./common/staff/StaffIdentityProvider";
import {ClientSelector} from "./common/ClientSelector";
import HomePage from "./HomePage";
import {ClientServiceProvider} from "./services/clients/ClientServiceProvider";
import NewMemberRegistration from "./components/membership/NewMemberRegistration";
import GuestSignupForm from "./components/membership/GuestSignupForm";
import TourSignup from "./components/membership/TourSignup";
import {PermissionsServiceProvider} from "./services/permissions/PermissionsServiceProvider";
import {UnauthorizedPage} from "./components/common/UnauthorizedPage";
import {ProtectedRoute} from "./common/api/ProtectedRoute";
import {FortiusProtectedRoute} from "./common/api/FortiusProtectedRoute";
import {AdminPermissions, BookingPermissions, ClubOwnerPermissions, MemberPermissions, PosScreenPermissions, StaffPermissions} from "./model/auth/Permissions";
import { MemberProvider, useMember } from "./common/member/MemberContext";
import { PointOfSaleServiceProvider } from "./services/pointofsale/PointOfSaleServiceProvider";
import { PromotionServiceProvider } from "./services/pointofsale/PromotionServiceProvider";
import { ReceiptPrinterServiceProvider } from "./services/printing/ReceiptPrinterServiceProvider";
import PointOfSale from "./components/pointofsale/PointOfSale";
import PromotionsManagement from "./components/pointofsale/PromotionsManagement";
import MemberProfileScreenV2 from "./components/membership/profile/MemberProfileScreenV2";
import { LoadingProvider } from "./common/loading/LoadingContext";
import { ClientConfigServiceProvider } from "./services/clients/ClientConfigServiceProvider";
import { ClientConfigProvider, useClientConfig } from "./common/client/ClientConfigProvider";
import { BrandingApplier } from "./common/branding/BrandingApplier";
import { TokensProvider, useTokens } from "./core/theme/TokensProvider";
import TransactionReconciliation from "./components/pointofsale/TransactionReconciliation";
import MemberReporting from "./components/membership/MemberReporting";
import CheckinReport from "./components/membership/CheckinReport";
import MemberRetentionScreen from "./components/membership/retention/MemberRetentionScreen";
import BusinessDevelopmentScreen from "./components/membership/businessdevelopment/BusinessDevelopmentScreen";
import RetentionReporting from "./components/membership/reporting/RetentionReporting";
import HandoverPanel from "./components/membership/dashboard/HandoverPanel";
import StaffIdentityPicker from "./components/membership/dashboard/StaffIdentityPicker";
import StaffDaily from "./components/membership/dashboard/StaffDaily";
import WorkAllocationRota from "./components/membership/dashboard/WorkAllocationRota";
import ShiftChecklistsManager from "./components/membership/dashboard/ShiftChecklistsManager";
import { MemberFollowupServiceProvider } from "./services/members/MemberFollowupServiceProvider";
import { MemberMetricsServiceProvider } from "./services/members/MemberMetricsServiceProvider";
import { StaffUserServiceProvider } from "./services/members/StaffUserServiceProvider";
import { RetentionDashboardServiceProvider } from "./services/members/RetentionDashboardServiceProvider";
import { ShiftChecklistServiceProvider } from "./services/members/ShiftChecklistServiceProvider";
import { RotaServiceProvider } from "./services/members/RotaServiceProvider";
import { CallsServiceProvider } from "./services/members/CallsServiceProvider";
import { BdOrganisationServiceProvider } from "./services/members/BdOrganisationServiceProvider";
import { useGlobalBarcodeScanner } from "./hooks/useGlobalBarcodeScanner";
import { useLogout } from "./hooks/useLogout";
import EnvironmentBanner from "./components/common/EnvironmentBanner";
import { FortiusClientServiceProvider } from "./services/fortius/FortiusClientServiceProvider";
import { SuperAdminClientList } from "./admin/clients/SuperAdminClientList";
import { CreateClientWizard } from "./admin/clients/CreateClientWizard";
import { SettingsShell } from "./admin/settings/SettingsShell";
import { PrimitivesDemo } from "./ui/primitives/PrimitivesDemo";

const {Header, Sider, Content} = Layout;

const LayoutWithRouter: React.FC = () => {
    useGlobalBarcodeScanner();

    // Sider background comes from the resolved tokens (branding overrides folded
    // in by the token-building layer). The logo is tenant *content*, not a design
    // token, so it stays on `branding`.
    const { branding } = useClientConfig();
    const sidebarBg = useTokens().color.sidebar.bg;

    // Fall back to the bundled Fortius logomark when the tenant has no
    // logo configured, or when the configured one fails to load. The
    // `logoLoadFailed` state resets whenever the configured URL changes
    // so a fixed-up setting takes effect without a hard reload.
    const [logoLoadFailed, setLogoLoadFailed] = useState(false);
    useEffect(() => { setLogoLoadFailed(false); }, [branding.logoUrl]);
    const usingFortiusFallback = !branding.logoUrl || logoLoadFailed;
    const logoSrc = usingFortiusFallback ? "/logo-square-dark.svg" : branding.logoUrl;

    const [collapsed, setCollapsed] = useState(false);
    const location = useLocation();
    const {user} = useAuth0();
    const screens = useBreakpoint();
    const isMobile = !screens.lg;
    const { isLoading: permissionsLoading, permissions, needsValidation, isInactiveMember, completionRequired, showValidationModal, setShowValidationModal, validateAuth, validationErrorMessage } = useMember();
    const navigate = useNavigate();
    const previousPathRef = useRef(location.pathname);
    const hasRedirectedRef = useRef(false);
    // Completion-eligible members are pinned to /my-profile with a blocking
    // prompt. Once they acknowledge it, the prompt hides so they can use the
    // self-service form (the save re-syncs and clears completionRequired).
    const [completionPromptDismissed, setCompletionPromptDismissed] = useState(false);

    // Users holding any staff / privileged role — Fortius Admin, Club Admin,
    // Front of House, Club Owner, Tour Signup, or Guest Signup — shouldn't be
    // auto-redirected from /home into the self-service /my-profile onboarding flow,
    // and they don't get the "My Profile" shortcut.
    // Only a bare fortiususer (a new self-service member with none of these
    // permissions) gets that treatment.
    const isPrivilegedUser = useMemo(() => {
        if (permissionsLoading || !permissions) return false;
        return permissions.permissions.some(p =>
            p.name === AdminPermissions.ClubAdmin ||
            p.name === AdminPermissions.FortiusAdmin ||
            p.name === AdminPermissions.FrontOfHouse ||
            p.name === StaffPermissions.PerformTasks ||
            p.name === ClubOwnerPermissions.ManageFollowups ||
            p.name === MemberPermissions.TourRegistration ||
            p.name === MemberPermissions.CanSignUpGuest
        );
    }, [permissionsLoading, permissions]);

    // Show validation modal on route change if user needs validation or is inactive
    useEffect(() => {
        if (previousPathRef.current !== location.pathname) {
            previousPathRef.current = location.pathname;
            if ((needsValidation || isInactiveMember) && !showValidationModal) {
                setShowValidationModal(true);
            }
        }
    }, [location.pathname, needsValidation, isInactiveMember, showValidationModal, setShowValidationModal]);

    // Completion-eligible members are blocked to /my-profile until they complete
    // their details. Any attempt to navigate elsewhere pins them back, and the
    // acknowledgement prompt re-shows so they can't wander the app.
    useEffect(() => {
        if (!completionRequired) return;
        if (location.pathname !== '/my-profile') {
            setCompletionPromptDismissed(false);
            navigate('/my-profile', { replace: true });
        }
    }, [completionRequired, location.pathname, navigate]);

    // Post-login redirect matrix (bare fortiususer members only; any privileged
    // / staff role is never redirected — see isPrivilegedUser). Runs once after
    // permissions load, only on /home:
    // - No membershipId → /my-profile?onboarding=1 (self-service create)
    // - membershipId, !isEnabled, status ∈ {Inactive,Pending,Cancelled} →
    //   /my-profile (blocking completion state, driven by completionRequired)
    // - membershipId, !isEnabled, status ∈ {Suspended,Banned} → no redirect;
    //   the "contact the club" dead-end modal (isInactiveMember) covers it
    // - Active member → no redirect
    useEffect(() => {
        if (permissionsLoading || hasRedirectedRef.current) return;
        if (location.pathname !== '/home') return;

        // Staff / privileged users aren't expected to have a member profile —
        // never force them into the self-service /my-profile flow.
        if (isPrivilegedUser) return;

        const membershipId = permissions?.member?.membershipId;
        const isEnabled = permissions?.member?.isEnabled;
        const status = permissions?.member?.status;

        if (!membershipId) {
            // No membership yet → self-service onboarding (blank create).
            hasRedirectedRef.current = true;
            navigate('/my-profile?onboarding=1', { replace: true });
        } else if (!isEnabled && (status === 'Inactive' || status === 'Pending' || status === 'Cancelled')) {
            // Completion-eligible → my-profile in a blocking completion state.
            hasRedirectedRef.current = true;
            navigate('/my-profile', { replace: true });
        }
    }, [permissionsLoading, permissions, location.pathname, navigate, isPrivilegedUser]);

    const handleLogout = useLogout();

    const baseMenuItems: MenuProps['items'] = useMemo(() => [
        {
            key: "/home",
            icon: <HomeOutlined/>,
            label: <Link to="/home">Home</Link>,
        },
    ], []);

// Function to check admin permissions
    const hasAdminPermission = useCallback(() => {
        if (permissionsLoading || !permissions) return false;
        return permissions.permissions.some(p =>
            p.name === AdminPermissions.ClubAdmin ||
            p.name === AdminPermissions.FortiusAdmin
        );
    }, [permissionsLoading, permissions]);

    const hasBookingPermission = useCallback(() => {
        if (permissionsLoading || !permissions) return false;
        return permissions.permissions.some(p =>
            p.name === BookingPermissions.Read
        );
    }, [permissionsLoading, permissions]);

    const isFrontOfHouse = useCallback(() => {
        if (permissionsLoading || !permissions) return false;
        return permissions.permissions.some(p =>
            p.name === AdminPermissions.FrontOfHouse ||
            p.name === AdminPermissions.FortiusAdmin
        );
    }, [permissionsLoading, permissions]);

    const hasClubOwnerPermission = useCallback(() => {
        if (permissionsLoading || !permissions) return false;
        return permissions.permissions.some(p =>
            p.name === ClubOwnerPermissions.ManageFollowups
        );
    }, [permissionsLoading, permissions]);

    // The club owner / GM tier (admin:club-owner). Distinct from hasClubOwnerPermission
    // above, which checks members:manage-followups — the retention screens' gate.
    const hasClubOwnerAdminPermission = useCallback(() => {
        if (permissionsLoading || !permissions) return false;
        return permissions.permissions.some(p =>
            p.name === AdminPermissions.ClubOwner
        );
    }, [permissionsLoading, permissions]);

    const hasFortiusAdminPermission = useCallback(() => {
        if (permissionsLoading || !permissions) return false;
        return permissions.permissions.some(p =>
            p.name === AdminPermissions.FortiusAdmin
        );
    }, [permissionsLoading, permissions]);

    // The Staff tier: reception accounts that reach the CRM without being club admins.
    const hasStaffPermission = useCallback(() => {
        if (permissionsLoading || !permissions) return false;
        return permissions.permissions.some(p => p.name === StaffPermissions.PerformTasks);
    }, [permissionsLoading, permissions]);

    const hasStaffCreatePermission = useCallback(() => {
        if (permissionsLoading || !permissions) return false;
        return permissions.permissions.some(p => p.name === StaffPermissions.CreateTasks);
    }, [permissionsLoading, permissions]);

    // Handover (staff messaging) rides in the header rather than a route, so it carries the
    // CRM gate itself. Same permissions as the CRM menu.
    const canUseCrm = useCallback(
        () => hasStaffPermission() || hasClubOwnerPermission() || hasFortiusAdminPermission(),
        [hasStaffPermission, hasClubOwnerPermission, hasFortiusAdminPermission]
    );

    // Handover and the staff picker ride in the header on both breakpoints, so build them
    // once rather than pasting the provider stack into each branch. Their providers are
    // nested here because the shared ones wrap <Content>, below the header.
    //
    // The picker sits next to Handover deliberately: it answers "whose inbox is this?",
    // which is the first question the Handover badge raises on a shared machine.
    const handover = canUseCrm() ? (
        <StaffUserServiceProvider>
            <RetentionDashboardServiceProvider>
                <StaffIdentityPicker/>
                <HandoverPanel/>
            </RetentionDashboardServiceProvider>
        </StaffUserServiceProvider>
    ) : null;

    const hasPermission = useCallback((permissionName: string) => {
        if (permissionsLoading || !permissions) return false;
        return permissions.permissions.some(p => p.name === permissionName);
    }, [permissionsLoading, permissions]);

// Generate full menu items based on permissions
    const menuItems = useMemo(() => {
        const items: MenuProps['items'] = [...(baseMenuItems || [])];

        // New Member form: admin/FoH only. Self-signup now goes through the
        // onboarding flow (/my-profile?onboarding=1), not this staff form.
        if (hasAdminPermission() || isFrontOfHouse()) {
            items.push({
                key: "/newmember",
                icon: <UserAddOutlined/>,
                label: <Link to="/newmember">New Member</Link>,
            });
        }

        // Create User: admin/FoH blank create, plus members:registerany
        // (kiosk/tablet accounts) which register members from a shared device.
        if (hasAdminPermission() || isFrontOfHouse() || hasPermission(MemberPermissions.RegisterAny)) {
            items.push({
                key: "/members/new",
                icon: <UserAddOutlined/>,
                label: <Link to="/members/new">Create User</Link>,
            });
        }

        // Guest Signup: visible to admin/FoH users OR users with members:createguest permission
        if (hasAdminPermission() || isFrontOfHouse() || hasPermission(MemberPermissions.CanSignUpGuest)) {
            items.push({
                key: "/guest-signup",
                icon: <UserAddOutlined/>,
                label: <Link to="/guest-signup">Guest Signup</Link>,
            });
        }

        // Tour Signup: the tour operator workspace (enquiry + pricing sheet + QR
        // sign-up). Visible to admins OR users with members:tour-registration.
        if (hasAdminPermission() || hasPermission(MemberPermissions.TourRegistration)) {
            items.push({
                key: "/tour-signup",
                icon: <CompassOutlined/>,
                label: <Link to="/tour-signup">Tour Signup</Link>,
            });
        }

        if (hasAdminPermission() || isFrontOfHouse()) {
            // Add members menu. Front-of-house reaches this block, so only push a child
            // whose route admits the current tier — a child whose route is stricter is
            // just a link to /unauthorized (FORT-383).
            const memberChildren: NonNullable<MenuProps['items']> = [
                {
                    key: "/profiles",
                    icon: <UserAddOutlined/>,
                    label: <Link to="/profiles">Manage Members</Link>,
                },
            ];

            // FORT-383: Member Reporting is the whole member list plus the active/expired
            // CSV exports, and stays admin-only. Its route admits admin:club/admin:fortius
            // alone, so listing it for front-of-house only ever offered reception a dead
            // link. Keep this condition and the route's in step, or the dead link returns.
            if (hasAdminPermission()) {
                memberChildren.push({
                    key: "/members/reporting",
                    icon: <BarChartOutlined/>,
                    label: <Link to="/members/reporting">Member Reporting</Link>
                });
            }

            memberChildren.push({
                key: "/members/checkins",
                icon: <BarChartOutlined/>,
                label: <Link to="/members/checkins">Club Check-ins</Link>
            });

            items.push({
                key: "members",
                icon: <UserAddOutlined/>,
                label: "Members",
                children: memberChildren,
            });

            if (hasAdminPermission()) {

                 // Add POS menu
                items.push({
                    key: "pos",
                    icon: <MoneyCollectFilled/>,
                    label: "Point of Sale",
                    children: [
                        {
                            key: "/pos",
                            icon: <MoneyCollectFilled/>,
                            label: <Link to="/pos">Point of Sale</Link>,
                        },                
                        {
                            key: "/pos/reconciliation",
                            icon: <MoneyCollectFilled/>,
                            label: <Link to="/pos/reconciliation">Reconciliation</Link>,
                        },
                        {
                            key: "/pos/promotions",
                            icon: <MoneyCollectFilled/>,
                            label: <Link to="/pos/promotions">Promotions</Link>,
                        }
                    ],
                });
            } else if (isFrontOfHouse()) {

                // Add POS menu
               items.push({
                   key: "pos",
                   icon: <MoneyCollectFilled/>,
                   label: "Point of Sale",
                   children: [
                       {
                           key: "/pos",
                           icon: <MoneyCollectFilled/>,
                           label: <Link to="/pos">Point of Sale</Link>,
                       } 
                   ],
               });
           }
            
           if (hasAdminPermission()) {
                // Add direct debit menu
                items.push({
                    key: "directdebits",
                    icon: <MoneyCollectFilled/>,
                    label: "Direct Debit",
                    children: [
                        {
                            key: "/directdebit",
                            icon: <MoneyCollectFilled/>,
                            label: <Link to="/directdebit">Member Direct Debits</Link>,
                        },
                        {
                            key: "/directdebit/runs",
                            icon: <MoneyCollectFilled/>,
                            label: <Link to="/directdebit/runs">Direct Debit Runs</Link>,
                        },
                    ],
                });
            }
        }

        if (hasBookingPermission()) {
            items.push({
                key: "/bookings/facilities",
                icon: <CalendarOutlined/>,
                label: <Link to="/bookings/facilities">Bookings</Link>
            });
        }

        // FORT-288: club admins set their own hourly booking prices.
        if (hasAdminPermission()) {
            items.push({
                key: "/bookings/pricing",
                icon: <CalendarOutlined/>,
                label: <Link to="/bookings/pricing">Booking Pricing</Link>
            });
        }

        // CRM: the retention / staff-workflow screens, grouped under one top-level menu
        // rather than sitting loose at the root. Children are gated individually, and the
        // parent only renders if at least one child survived its gate.
        const crmChildren: NonNullable<MenuProps['items']> = [];

        if (hasStaffPermission() || hasClubOwnerPermission() || hasFortiusAdminPermission()) {
            crmChildren.push({
                key: "/members/retention",
                icon: <BarChartOutlined/>,
                label: <Link to="/members/retention">Member Retention</Link>
            });
            crmChildren.push({
                key: "/members/business-development",
                icon: <BarChartOutlined/>,
                label: <Link to="/members/business-development">Business Dev CRM</Link>
            });
            crmChildren.push({
                key: "/members/staff-daily",
                icon: <CheckSquareOutlined/>,
                label: <Link to="/members/staff-daily">Staff Daily</Link>
            });
        }

        // FORT-377: Retention Reporting is the club's own retention figures, and the client
        // asked that reception not have them. Same condition as the API's
        // CanViewRetentionReporting — the two must move together, or a reception user either
        // sees a link to /unauthorized or reaches a screen whose every request 403s.
        //
        // hasClubOwnerPermission() (members:manage-followups) is deliberately absent: every
        // reception account holds it, so admitting it here would leave the item as visible as
        // before. hasAdminPermission() already covers admin:fortius, so it isn't repeated.
        //
        // Spliced back to its original slot rather than appended: for a manager the menu order
        // is unchanged by this ticket, and moving a familiar item is a second, unasked-for
        // change riding along with a permission one.
        if (hasStaffCreatePermission() || hasClubOwnerAdminPermission() || hasAdminPermission()) {
            const staffDailyIndex = crmChildren.findIndex(
                (child) => child && "key" in child && child.key === "/members/staff-daily"
            );
            const reportingItem = {
                key: "/members/retention-reporting",
                icon: <BarChartOutlined/>,
                label: <Link to="/members/retention-reporting">Retention Reporting</Link>
            };
            if (staffDailyIndex === -1) crmChildren.push(reportingItem);
            else crmChildren.splice(staffDailyIndex, 0, reportingItem);
        }

        // Work Allocation creates and assigns work, so it stays manager-tier — a Staff
        // account reaches the CRM and Staff Daily but not this.
        //
        // Deliberately NOT hasClubOwnerPermission() (members:manage-followups): the API's
        // CanCreateStaffTasks doesn't honour the legacy permission, so admitting it here
        // would show the menu item to every reception account and 403 them on arrival.
        if (hasStaffCreatePermission() || hasFortiusAdminPermission()) {
            crmChildren.push({
                key: "/members/rota",
                icon: <TeamOutlined/>,
                label: <Link to="/members/rota">Work Allocation</Link>
            });
        }

        // Shift checklist templates are club-owner-managed (admin:club-owner or Fortius
        // admin) — a narrower gate than the rest of CRM, which is why it's checked
        // separately rather than folded into the block above.
        if (hasClubOwnerAdminPermission() || hasFortiusAdminPermission()) {
            crmChildren.push({
                key: "/members/shift-checklists",
                icon: <CheckSquareOutlined/>,
                label: <Link to="/members/shift-checklists">Shift Checklists</Link>
            });
        }

        if (crmChildren.length > 0) {
            items.push({
                key: "crm",
                icon: <TeamOutlined/>,
                label: "CRM",
                children: crmChildren,
            });
        }


        // Add My Profile only for a bare member user (has a membershipId and no
        // staff/privileged role). Staff aren't expected to own a profile.
        if (!isPrivilegedUser && permissions?.member?.membershipId) {
            items.push({
                key: "/my-profile",
                icon: <UserOutlined />,
                label: <Link to="/my-profile">My Profile</Link>,
            });
        }

        // Add logout at the end
        items.push({
            key: "logout",
            label: "Logout",
            onClick: handleLogout
        });

        return items;
    }, [handleLogout, baseMenuItems, hasAdminPermission, hasBookingPermission, isFrontOfHouse, hasPermission, hasClubOwnerPermission, hasClubOwnerAdminPermission, hasFortiusAdminPermission, hasStaffPermission, hasStaffCreatePermission, permissions?.member?.membershipId, isPrivilegedUser]);

    const Logo: React.FC<{ collapsed?: boolean }> = ({collapsed}) => {
        const screens = useBreakpoint();
        const isMobile = !screens.lg;

        return (
            <div
                style={{
                    padding: isMobile ? "8px 0" : "16px",
                    textAlign: "center",
                    transition: "all 0.2s",
                    height: isMobile ? "64px" : "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <img
                    src={logoSrc}
                    alt={usingFortiusFallback ? "Fortius Technology" : "Club Logo"}
                    onError={() => {
                        if (!logoLoadFailed) setLogoLoadFailed(true);
                    }}
                    style={{
                        height: isMobile ? "48px" : collapsed ? "32px" : "64px",
                        width: "auto",
                        objectFit: "contain",
                        maxWidth: "100%",
                        transition: "all 0.2s",
                    }}
                />
            </div>
        );
    };

    const MobileMenu: React.FC = () => {
        const dropdownMenu = (
            <Menu items={menuItems} selectedKeys={[location.pathname]}/>
        );

        return (
            <Dropdown
                overlay={dropdownMenu}
                trigger={["click"]}
                overlayStyle={{
                    maxHeight: "80vh",
                    overflow: "auto",
                }}
            >
                <Button
                    type="text"
                    icon={<MenuOutlined/>}
                    style={{
                        color: "white",
                        fontSize: "20px",
                        padding: "8px 12px",
                    }}
                />
            </Dropdown>
        );
    };

    const UserMenu: React.FC = () => {
        const userEmail = user?.email ?? user?.name ?? "Account";

        const items: MenuProps["items"] = [
            {
                key: "email",
                label: userEmail,
                disabled: true,
            },
            ...(!isPrivilegedUser && permissions?.member?.membershipId
                ? [{
                    key: "/my-profile",
                    icon: <UserOutlined/>,
                    label: <span data-testid="user-menu-my-profile">My Profile</span>,
                    onClick: () => navigate("/my-profile"),
                }]
                : []),
            {
                key: "logout",
                icon: <LogoutOutlined/>,
                label: <span data-testid="user-menu-logout">Logout</span>,
                onClick: handleLogout,
            },
        ];

        return (
            <Dropdown menu={{items}} trigger={["click"]} placement="bottomRight">
                <Button
                    type="text"
                    data-testid="user-menu-trigger"
                    icon={<UserOutlined/>}
                    style={{
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        maxWidth: "240px",
                    }}
                >
                    <span
                        data-testid="user-menu-email"
                        style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {userEmail}
                    </span>
                    <DownOutlined style={{fontSize: "10px"}}/>
                </Button>
            </Dropdown>
        );
    };

    return (
        <>
        <EnvironmentBanner />
        <Layout style={{minHeight: "100vh"}}>
            {/* Desktop Sider - Hidden on mobile */}
            {!isMobile && (
                <Sider
                    collapsible
                    collapsed={collapsed}
                    onCollapse={setCollapsed}
                    style={{background: sidebarBg}}
                    width={250}
                >
                    <Logo collapsed={collapsed}/>
                    <Menu
                        theme="dark"
                        mode="inline"
                        selectedKeys={[location.pathname]}
                        style={{
                            background: sidebarBg,
                            borderRight: 0,
                            color: "white",
                        }}
                        items={menuItems}
                    />
                </Sider>
            )}

            <Layout>
                <Header
                    style={{
                        padding: "0 16px",
                        background: sidebarBg,
                        display: "flex",
                        alignItems: "center",
                        height: "64px",
                        position: "sticky",
                        top: 0,
                        zIndex: 1000,
                    }}
                >
                    {isMobile ? (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                height: "64px",
                            }}
                        >
                            <MobileMenu/>
                            <div
                                style={{
                                    flex: 1,
                                    display: "flex",
                                    justifyContent: "center",
                                    overflow: "hidden",
                                }}
                            >
                                <Logo/>
                            </div>
                            {handover}
                            {needsValidation && (
                                <Button
                                    type="primary"
                                    icon={<SyncOutlined />}
                                    onClick={() => setShowValidationModal(true)}
                                    style={{
                                        marginLeft: "8px",
                                        background: "#52c41a",
                                        borderColor: "#52c41a",
                                    }}
                                >
                                    Sync
                                </Button>
                            )}
                        </div>
                    ) : (
                        // Add this block for desktop view
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                            }}
                        >
                            <div/>
                            {/* Empty div for spacing */}
                            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                {needsValidation && (
                                    <Button
                                        type="primary"
                                        icon={<SyncOutlined />}
                                        onClick={() => setShowValidationModal(true)}
                                        style={{
                                            background: "#52c41a",
                                            borderColor: "#52c41a",
                                        }}
                                    >
                                        Synchronise
                                    </Button>
                                )}
                                {handover}
                                <ClientSelector/>
                                {/* Desktop only — on mobile the email/My Profile/
                                    Logout affordances live in the sidebar menu. */}
                                <UserMenu/>
                            </div>
                            <div style={{paddingRight: "16px"}}/>
                        </div>
                    )}
                </Header>

                <Content
                    style={{
                        margin: isMobile ? "16px 8px" : "24px 16px",
                        padding: isMobile ? 16 : 24,
                        minHeight: 280,
                    }}
                >
                    <PermissionsServiceProvider>
                        <ClientServiceProvider>
                                <BookingServiceProvider>
                                        <MemberServiceProvider>
                                        <MemberFollowupServiceProvider>
                                        <MemberMetricsServiceProvider>
                                        <StaffUserServiceProvider>
                                        <RetentionDashboardServiceProvider>
                                        <ShiftChecklistServiceProvider>
                                        <RotaServiceProvider>
                                        <CallsServiceProvider>
                                        <BdOrganisationServiceProvider>
                                        <PointOfSaleServiceProvider>
                                        <ReceiptPrinterServiceProvider>
                                        <PaymentServiceProvider>
                                        <RecurringMemberItemsServiceProvider>
                                        <FortiusClientServiceProvider>
                                        <PromotionServiceProvider>
                                        <Routes>
                                                <Route path="/home" element={
                                                    <ProtectedRoute>
                                                        <HomePage/>
                                                    </ProtectedRoute>
                                                }/>
                                                <Route path="/bookings/facilities" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        AdminPermissions.ClubAdmin,
                                                        AdminPermissions.FortiusAdmin,
                                                        AdminPermissions.FrontOfHouse,
                                                        BookingPermissions.Cancel,
                                                        BookingPermissions.Create,
                                                        BookingPermissions.Edit,
                                                        BookingPermissions.Read
                                                    ]}>
                                                        <FacilityBookingScheduleV2/>
                                                    </ProtectedRoute>
                                                }/>
                                                {/* FORT-288: matches the API's CanManageBookingPricing
                                                    (admin:club, or admin:fortius via the bypass). */}
                                                <Route path="/bookings/pricing" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        AdminPermissions.ClubAdmin,
                                                        AdminPermissions.FortiusAdmin
                                                    ]}>
                                                        <BookingPricingManagement/>
                                                    </ProtectedRoute>
                                                }/>
                                                <Route path="/profiles" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        AdminPermissions.ClubAdmin,
                                                        AdminPermissions.FortiusAdmin,
                                                        AdminPermissions.FrontOfHouse,
                                                    ]}>
                                                        <MemberProfileScreenV2 />
                                                    </ProtectedRoute>
                                                }/>
                                                <Route path="/members/new" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        AdminPermissions.ClubAdmin,
                                                        AdminPermissions.FortiusAdmin,
                                                        AdminPermissions.FrontOfHouse,
                                                        MemberPermissions.RegisterAny,
                                                    ]}>
                                                        <MemberProfileScreenV2 entry="adminCreate" />
                                                    </ProtectedRoute>
                                                }/>
                                                <Route path="/profiles/:membershipId" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        AdminPermissions.ClubAdmin,
                                                        AdminPermissions.FortiusAdmin,
                                                        AdminPermissions.FrontOfHouse,
                                                    ]}>
                                                        <MemberProfileScreenV2 />
                                                    </ProtectedRoute>
                                                }/>
                                                <Route path="/my-profile" element={
                                                    <ProtectedRoute requireMember allowWithoutMember>
                                                        <MemberProfileScreenV2 selfServiceMode={true} />
                                                    </ProtectedRoute>
                                                }/>
                                                <Route path="/newmember" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        MemberPermissions.SubmitNewMemberForm,
                                                        AdminPermissions.ClubAdmin,
                                                        AdminPermissions.FortiusAdmin,
                                                        AdminPermissions.FrontOfHouse
                                                    ]} allowWithoutMember>
                                                        <NewMemberRegistration/>
                                                    </ProtectedRoute>
                                                }/>
                                                <Route path="/guest-signup" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        MemberPermissions.CanSignUpGuest,
                                                        AdminPermissions.ClubAdmin,
                                                        AdminPermissions.FortiusAdmin,
                                                        AdminPermissions.FrontOfHouse
                                                    ]}>
                                                        <GuestSignupForm />
                                                    </ProtectedRoute>
                                                }/>
                                                <Route path="/tour-signup" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        MemberPermissions.TourRegistration,
                                                        AdminPermissions.ClubAdmin,
                                                        AdminPermissions.FortiusAdmin
                                                    ]}>
                                                        <TourSignup />
                                                    </ProtectedRoute>
                                                }/>
                                                <Route path="/directdebit" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        AdminPermissions.ClubAdmin,
                                                        AdminPermissions.FortiusAdmin
                                                    ]}>
                                                        <DirectDebitManagement/>
                                                    </ProtectedRoute>
                                                }/>
                                                <Route path="/directdebit/runs" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        AdminPermissions.ClubAdmin,
                                                        AdminPermissions.FortiusAdmin
                                                    ]}>
                                                        <DirectDebitRunManager/>
                                                    </ProtectedRoute>
                                                }/>
                                                {/* PosScreenPermissions is shared with the member-profile Point of Sale quick action, so the two gates cannot drift. */}
                                                <Route path="/pos" element={
                                                    <ProtectedRoute requiredPermission={PosScreenPermissions}>
                                                        <PointOfSale />
                                                    </ProtectedRoute>
                                                }/>

                                                <Route path="/pos/reconciliation" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        AdminPermissions.ClubAdmin,
                                                        AdminPermissions.FortiusAdmin
                                                    ]}>
                                                        <TransactionReconciliation />
                                                    </ProtectedRoute>
                                                }/>

                                                <Route path="/pos/promotions" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        AdminPermissions.ClubAdmin,
                                                        AdminPermissions.FortiusAdmin
                                                    ]}>
                                                        <PromotionsManagement />
                                                    </ProtectedRoute>
                                                }/>
                                                <Route path="/members/reporting" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        AdminPermissions.ClubAdmin,
                                                        AdminPermissions.FortiusAdmin
                                                    ]}>
                                                        <MemberReporting />
                                                    </ProtectedRoute>
                                                }/>
                                                {/* FORT-383: the Members menu has always
                                                    offered reception this link, and the only
                                                    call the screen makes — GET /members/checkins
                                                    — is on CanReadAllMemberData. Admitting only
                                                    admin:club meant the route, not the API, was
                                                    turning front-of-house away.

                                                    ReadAllMembers is listed alongside the roles
                                                    so the gate mirrors what the API actually
                                                    enforces rather than approximating it: an
                                                    account whose grants drift keeps a clean
                                                    /unauthorized instead of a screen whose one
                                                    call 403s behind an error toast. */}
                                                <Route path="/members/checkins" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        AdminPermissions.ClubAdmin,
                                                        AdminPermissions.FortiusAdmin,
                                                        AdminPermissions.FrontOfHouse,
                                                        MemberPermissions.ReadAllMembers,
                                                    ]}>
                                                        <CheckinReport />
                                                    </ProtectedRoute>
                                                }/>
                                                {/* No admin:club here. Every endpoint this screen
                                                    calls is on CanPerformStaffTasks, which doesn't
                                                    accept it, and only admin:fortius bypasses the
                                                    permission check server-side — so a club admin
                                                    holding neither reached a screen where every
                                                    request 403'd. It was never a working route for
                                                    them; the CRM menu doesn't offer it either. */}
                                                <Route path="/members/retention" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        StaffPermissions.PerformTasks,
                                                        ClubOwnerPermissions.ManageFollowups,
                                                        AdminPermissions.FortiusAdmin
                                                    ]}>
                                                        <MemberRetentionScreen />
                                                    </ProtectedRoute>
                                                }/>
                                                <Route path="/members/business-development" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        StaffPermissions.PerformTasks,
                                                        ClubOwnerPermissions.ManageFollowups,
                                                        AdminPermissions.FortiusAdmin
                                                    ]}>
                                                        <BusinessDevelopmentScreen />
                                                    </ProtectedRoute>
                                                }/>
                                                {/* FORT-377: management/admin only, matching the
                                                    API's CanViewRetentionReporting. Neither
                                                    staff:tasks:perform nor the legacy
                                                    members:manage-followups appears here —
                                                    reception holds both, so either would leave
                                                    the screen as open as it was. */}
                                                <Route path="/members/retention-reporting" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        StaffPermissions.CreateTasks,
                                                        AdminPermissions.ClubOwner,
                                                        AdminPermissions.ClubAdmin,
                                                        AdminPermissions.FortiusAdmin
                                                    ]}>
                                                        <RetentionReporting />
                                                    </ProtectedRoute>
                                                }/>
                                                <Route path="/members/staff-daily" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        StaffPermissions.PerformTasks,
                                                        ClubOwnerPermissions.ManageFollowups,
                                                        AdminPermissions.FortiusAdmin
                                                    ]}>
                                                        <StaffDaily />
                                                    </ProtectedRoute>
                                                }/>
                                                {/* Manager tier, and no legacy fallback — the
                                                    API's CanCreateStaffTasks doesn't honour
                                                    members:manage-followups, so letting it
                                                    through here would render a screen whose
                                                    every call 403s. */}
                                                <Route path="/members/rota" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        StaffPermissions.CreateTasks,
                                                        AdminPermissions.FortiusAdmin
                                                    ]}>
                                                        <WorkAllocationRota />
                                                    </ProtectedRoute>
                                                }/>
                                                <Route path="/members/shift-checklists" element={
                                                    <ProtectedRoute requiredPermission={[
                                                        AdminPermissions.ClubOwner,
                                                        AdminPermissions.FortiusAdmin
                                                    ]}>
                                                        <ShiftChecklistsManager />
                                                    </ProtectedRoute>
                                                }/>
                                            </Routes>
                                        </PromotionServiceProvider>
                                        </FortiusClientServiceProvider>
                                        </RecurringMemberItemsServiceProvider>
                                        </PaymentServiceProvider>
                                        </ReceiptPrinterServiceProvider>
                                        </PointOfSaleServiceProvider>
                                        </BdOrganisationServiceProvider>
                                        </CallsServiceProvider>
                                        </RotaServiceProvider>
                                        </ShiftChecklistServiceProvider>
                                        </RetentionDashboardServiceProvider>
                                        </StaffUserServiceProvider>
                                        </MemberMetricsServiceProvider>
                                        </MemberFollowupServiceProvider>
                                    </MemberServiceProvider>
                                </BookingServiceProvider>
                        </ClientServiceProvider>
                    </PermissionsServiceProvider>
                </Content>
            </Layout>
            <MembershipValidationModal
                visible={showValidationModal || (completionRequired && !completionPromptDismissed && location.pathname === '/my-profile')}
                onValidate={validateAuth}
                onCancel={() => setShowValidationModal(false)}
                errorMessage={validationErrorMessage}
                isInactiveMember={isInactiveMember}
                completionRequired={completionRequired && location.pathname === '/my-profile'}
                onCompleteDetails={() => setCompletionPromptDismissed(true)}
            />
        </Layout>
        </>
    );
};

const ThemedLayout: React.FC = () => {
    // All theme colours come from the tenant-resolved tokens; ThemedLayout no
    // longer reads `branding.*` directly (FORT-180).
    const theme = useTokens();
    const submenuBg = theme.color.sidebar.submenuBg;
    const selectedBg = theme.color.sidebar.itemSelectedBg;
    const menuOverrides = `
        .ant-menu-sub.ant-menu-inline {
            background: ${submenuBg} !important;
        }
        .ant-menu-item-selected {
            background-color: ${selectedBg} !important;
        }
        .ant-menu-dark .ant-menu-submenu-selected > .ant-menu-submenu-title {
            color: #fff !important;
        }
    `;
    return (
        <ConfigProvider
            theme={{
                // Global theme is sourced from the tenant-resolved design tokens
                // (FORT-179 + FORT-180). colorPrimary carries the branding
                // override; the rest mirror Ant Design's defaults.
                token: {
                    colorPrimary: theme.color.primary,
                    colorError: theme.color.danger,
                    colorSuccess: theme.color.success,
                    colorWarning: theme.color.warning,
                    borderRadius: theme.radius.md,
                    fontSize: theme.fontSize.sm,
                },
            }}
        >
            <style>{menuOverrides}</style>
            <BrandingApplier />
            <LayoutWithRouter/>
        </ConfigProvider>
    );
};

const ClientConfigGate: React.FC = () => (
    <ClientConfigServiceProvider>
        <ClientConfigProvider>
            <TokensProvider>
                <ThemedLayout/>
            </TokensProvider>
        </ClientConfigProvider>
    </ClientConfigServiceProvider>
);

// Cross-tenant admin shell. Rendered for /fortius/* routes outside the
// ClientConfigProvider gate so a Fortius admin can still reach client
// management even when the currently-selected tenant has no row in dbo.client
// (e.g. a stale Auth0 claim, or a tenant that exists in Auth0 but hasn't
// been onboarded yet). Uses minimal chrome — no per-tenant branding, no
// member-scoped sidebar — because the active tenant may be unresolvable.
const FortiusAdminLayout: React.FC = () => {
    const handleLogout = useLogout();
    const navigate = useNavigate();
    return (
        <>
            <EnvironmentBanner/>
            <Layout style={{minHeight: '100vh'}}>
                <Header
                    style={{
                        background: '#00005a',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 24px',
                        height: 64,
                    }}
                >
                    <div style={{color: 'white', fontWeight: 600, fontSize: 16}}>
                        Fortius Admin
                    </div>
                    <div style={{display: 'flex', gap: 12}}>
                        <Button type="text" style={{color: 'white'}} onClick={() => navigate('/home')}>
                            Back to app
                        </Button>
                        <Button
                            type="text"
                            style={{color: 'white'}}
                            onClick={handleLogout}
                        >
                            Logout
                        </Button>
                    </div>
                </Header>
                <Content style={{margin: '24px 16px', padding: 24, minHeight: 280}}>
                    <PermissionsServiceProvider>
                        <ClientServiceProvider>
                            <ClientConfigServiceProvider>
                                <FortiusClientServiceProvider>
                                    <Routes>
                                        <Route index element={<Navigate to="clients" replace/>}/>
                                        <Route path="clients" element={
                                            <FortiusProtectedRoute>
                                                <SuperAdminClientList/>
                                            </FortiusProtectedRoute>
                                        }/>
                                        <Route path="clients/new" element={
                                            <FortiusProtectedRoute>
                                                <CreateClientWizard/>
                                            </FortiusProtectedRoute>
                                        }/>
                                        <Route path="clients/:clientId/settings" element={
                                            <FortiusProtectedRoute>
                                                <SettingsShell/>
                                            </FortiusProtectedRoute>
                                        }/>
                                    </Routes>
                                </FortiusClientServiceProvider>
                            </ClientConfigServiceProvider>
                        </ClientServiceProvider>
                    </PermissionsServiceProvider>
                </Content>
            </Layout>
        </>
    );
};

const AuthenticatedRouter: React.FC = () => (
    <Routes>
        <Route path="/fortius/*" element={<FortiusAdminLayout/>}/>
        <Route path="/unauthorized" element={<UnauthorizedPage/>}/>
        <Route path="/*" element={<ClientConfigGate/>}/>
    </Routes>
);

const ProtectedLayout = withAuthenticationRequired(AuthenticatedRouter);

// The authenticated app. ClientProvider + MemberProvider live here — NOT around
// the whole tree — so the public pages (landing, /clubs) never mount them and
// therefore never trigger authenticated bootstrap calls like /auth/sync.
const AuthenticatedApp: React.FC = () => (
    <ClientProvider>
        <MemberProvider>
            {/* Which staff member this device is signed in as. Inside ClientProvider
                because a selection only means anything within one tenant's roster. */}
            <StaffIdentityProvider>
                <ProtectedLayout/>
            </StaffIdentityProvider>
        </MemberProvider>
    </ClientProvider>
);

// The site root. On the apex (no tenant slug, including localhost) it is the
// public marketing landing page — logging in happens only after choosing a club
// on /clubs and arriving on that club's subdomain, never from the apex root. On
// a tenant subdomain the root is the authenticated app, as before.
const RootRoute: React.FC = () =>
    extractSubdomainSlug(window.location.hostname) === null
        ? <LandingPage/>
        : <AuthenticatedApp/>;

const Auth0ErrorHandler: React.FC = () => {
    const {error} = useAuth0();

    if (error) {
        return (
            <div>
                <h1>An error occurred</h1>
                <p>{error.message}</p>
            </div>
        );
    }

    return null;
};

const App: React.FC = () => {
    return (
        <>
            <LoadingProvider>
            <Auth0ProviderWithConfig>
                <Router>
                    <Auth0ErrorHandler/>
                    <Routes>
                        {/* Public routes render outside ClientProvider/MemberProvider
                            (and withAuthenticationRequired): they need no tenant or
                            login, and must not fire authenticated bootstrap calls
                            such as /auth/sync. The apex root is the marketing
                            landing; the app (and per-subdomain login) lives under
                            AuthenticatedApp at /home etc. */}
                        <Route path="/clubs" element={<ClubsPage/>}/>
                        {/* Dev-only, no-auth gallery of the Tier-2 primitives
                            (FORT-182). Self-supplies tokens via StaticTokensProvider. */}
                        {process.env.NODE_ENV !== 'production' && (
                          <Route path="/dev/primitives" element={<PrimitivesDemo/>}/>
                        )}
                        <Route path="/" element={<RootRoute/>}/>
                        <Route path="/*" element={<AuthenticatedApp/>}/>
                    </Routes>
                </Router>
            </Auth0ProviderWithConfig>
            </LoadingProvider>
        </>
    );
};

export default App;
